type SessionActor = {
  userName?: string | null;
  userLastName?: string | null;
  userEmail?: string | null;
};

type SessionAccount = SessionActor & {
  systemRole?: string | null;
  superAdmin?: unknown;
};

export type AdminReporter = { email?: string; name?: string };

/**
 * Whether a Lime admin is the person behind this session.
 *
 * The two shapes are disjoint: `systemRole` describes the account that is
 * loaded, so it reads "admin" only in an admin's own session, while
 * `/client` populates `superAdmin` only for an impersonated session, where it
 * names the admin actor driving it. Either check alone misses half of them.
 */
export function isLimeAdminSession(
  accountInfo: SessionAccount | undefined | null,
): accountInfo is SessionAccount {
  return (
    accountInfo?.systemRole === "admin" || Boolean(accountInfo?.superAdmin)
  );
}

/**
 * Who is driving the session. Under impersonation the account fields describe
 * the salon user being viewed, so using them would both misname the reporter
 * and hand a customer's address to a third party; `superAdmin` carries the Lime
 * admin actually driving it.
 */
export function resolveSessionActor(accountInfo: SessionAccount): SessionActor {
  const actor = accountInfo.superAdmin;
  if (actor && typeof actor === "object") return actor as SessionActor;
  return accountInfo;
}

export function adminReporter(accountInfo: SessionAccount): AdminReporter {
  const actor = resolveSessionActor(accountInfo);
  const name = [actor.userName, actor.userLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return {
    email: actor.userEmail ?? undefined,
    name: name || undefined,
  };
}
